import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "../components/header/Header";
import Footer from "../components/footer";
import Gotop from "../components/gotop";
import { positionAPI } from "../services/api";
import { getJobLocation, getSalaryDisplay, getCountriesDisplay } from "../utils/jobDisplay";
import "./JobDetails.scss";

function JobDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchJobDetails();
  }, [id]);

  useEffect(() => {
    if (job) {
      document.title = `${job.title} at ${job.company} - Talsolution`;
    }
    return () => { document.title = "Talsolution"; };
  }, [job]);

  const fetchJobDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await positionAPI.getById(id);
      if (response.data.success) {
        setJob(response.data.data);
      }
    } catch (err) {
      setError("Failed to load job details. Please try again later.");
      console.error("Error fetching job details:", err);
    } finally {
      setLoading(false);
    }
  };

  const calculateDaysAgo = (date) => {
    const days = Math.floor((new Date() - new Date(date)) / (1000 * 60 * 60 * 24));
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    return `${days} days ago`;
  };

  const calculateDaysLeft = (deadline) => {
    if (!deadline) return null;
    const days = Math.floor((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return "Expired";
    if (days === 0) return "Last day";
    return `${days} days left to apply`;
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="text-center py-5">
          <div className="app-spinner" style={{ margin: '0 auto 16px' }} />
          <p>Loading job details...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !job) {
    return (
      <>
        <Header />
        <div className="text-center py-5">
          <p>{error || "Job not found"}</p>
          <Link to="/careers" className="btn btn-primary mt-3">
            Back to Careers
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const salaryDisplay = getSalaryDisplay(job);
  const showSalary = salaryDisplay && !job.salaryRange?.hideFromPublic;
  const workTypeStr = Array.isArray(job.workType) && job.workType.length > 0 ? job.workType.join(", ") : "";
  const countriesStr = getCountriesDisplay(job);
  const locationLine = [workTypeStr, countriesStr].filter(Boolean).join(" - ");
  const omitLocationFromMeta = / - Global$/i.test(locationLine);
  const keyFacts = [
    job.company,
    job.department,
    job.employmentType,
    ...(locationLine && !omitLocationFromMeta ? [locationLine] : []),
  ].filter(Boolean);
  const salaryCurrency = (job.salaryRange?.currency || "USD").toUpperCase();
  const salaryRangeText = showSalary
    ? `${salaryDisplay.amountText} ${salaryCurrency}`
    : null;
  const salaryIntro =
    !showSalary ? null
    : (job.payType || "").toString().toLowerCase() === "hourly"
      ? "The hourly rate range for this position is:"
    : (job.payType || "").toString().toLowerCase() === "monthly"
      ? "The base monthly salary range for this position is:"
    : (job.payType || "").toString().toLowerCase() === "yearly"
      ? "The base salary range for this position is:"
      : "The base salary range for this position is:";

  return (
    <>
      <Header />

      <section className="job-details-simple">
        <div className="job-details-simple-inner">
          <button
            type="button"
            className="back-button"
            onClick={() => navigate("/careers")}
          >
            <i className="icon-keyboard_arrow_left" />
            Back to careers
          </button>

          <header className="job-details-simple-header">
            <div className="job-details-simple-headline">
              <h1 className="job-details-simple-title">{job.title}</h1>
              {locationLine && !omitLocationFromMeta && (
                <p className="job-details-simple-location">
                  <i className="icon-map-pin" />
                  {locationLine}
                </p>
              )}
            </div>
          </header>

          {keyFacts.length > 0 && (
            <p className="job-details-simple-meta">
              {keyFacts.join(" · ")}
            </p>
          )}

          <div
            className="job-details-simple-content"
            dangerouslySetInnerHTML={{ __html: job.description }}
          />

          {salaryRangeText && salaryIntro && (
            <div className="job-details-simple-salary">
              <p className="job-details-simple-salary-label">{salaryIntro}</p>
              <p className="job-details-simple-salary-value">{salaryRangeText}</p>
            </div>
          )}

          {job.note && (
            <div className="job-details-simple-note">
              <h2 className="job-details-simple-heading">Additional information</h2>
              <p>{job.note}</p>
            </div>
          )}

          <div className="job-details-simple-legal">
            <p>
              In select roles and locations, interviews may be recorded, transcribed, or summarized using technology to support our hiring process. You will have the opportunity to opt out of recording, transcription, or summarization prior to any scheduled interviews.
            </p>
            <p>
              During the process, we may collect identifiers, professional and employment-related information, and, where applicable, sensory information (such as audio or video). We use this information to evaluate your application for employment or an independent contractor role. We do not sell your personal information or disclose it to third parties for their marketing purposes. Any recording of your interview will be deleted promptly after a hiring decision. For more information about how we handle your personal information, please refer to our{" "}
              <Link to="/privacypolicy">Privacy Policy</Link> and{" "}
              <Link to="/termsofuse">Terms of Use</Link>.
            </p>
            <p>
              Talsolution is an equal opportunity employer and is committed to building a workforce that reflects the diverse communities we serve. We are committed to providing reasonable accommodations for qualified individuals with disabilities in our job application and interview procedures. If you need an accommodation during the process, please let your recruiter know or contact us.
            </p>
          </div>

          <div className="job-details-simple-cta">
            <button
              onClick={() => navigate(`/careers/${job._id}/apply`)}
              className="job-details-simple-apply job-details-simple-apply-bottom"
            >
              Apply
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <Gotop />

    </>
  );
}

export default JobDetails;
